import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  currentStep: 1,
  totalSteps: 7,
  isCreating: false,
  recentProjects: [],
  breadcrumbs: [],
  showValidationErrors: false,
  selectedPreviewPageId: null,
  sidebarOpen: true,
};

const navigationSlice = createSlice({
  name: "navigation",
  initialState,
  reducers: {
    setStep: (state, action) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      state.currentStep = Math.min(state.currentStep + 1, state.totalSteps);
      state.showValidationErrors = false;
    },
    prevStep: (state) => {
      state.currentStep = Math.max(state.currentStep - 1, 1);
      state.showValidationErrors = false;
    },
    showValidationErrors: (state) => {
      state.showValidationErrors = true;
    },
    hideValidationErrors: (state) => {
      state.showValidationErrors = false;
    },
    setIsCreating: (state, action) => {
      state.isCreating = Boolean(action.payload);
    },
    setRecentProjects: (state, action) => {
      state.recentProjects = Array.isArray(action.payload)
        ? action.payload
        : [];
    },
    addRecentProject: (state, action) => {
      const incoming = action.payload || {};
      const id =
        incoming.id || incoming.Id || incoming.projectId || incoming.ProjectId;
      let name =
        incoming.name ||
        incoming.Name ||
        incoming.projectName ||
        incoming.ProjectName;
      if (!name && incoming.metadata) {
        name = incoming.metadata.name || incoming.metadata.shortName || "";
      }
      if (!id || !name) return;

      const existing = state.recentProjects.filter((p) => p.id !== id);
      state.recentProjects = [{ id, name }, ...existing].slice(0, 10);
    },
    setBreadcrumbs: (state, action) => {
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      state.breadcrumbs = incoming;
    },
    resetBreadcrumbs: (state) => {
      state.breadcrumbs = [];
    },
    trimBreadcrumbs: (state, action) => {
      const targetPath = action.payload;
      if (typeof targetPath !== "string") return;
      const idx = state.breadcrumbs.findIndex(
        (c) => c && c.path === targetPath,
      );
      if (idx === -1) return;
      state.breadcrumbs = state.breadcrumbs.slice(0, idx + 1);
    },
    pushBreadcrumb: (state, action) => {
      const item = action.payload || {};
      if (!item || typeof item.path !== "string") return;
      const { id, label, path, type } = item;

      // If path already exists, trim to it
      const existingIdx = state.breadcrumbs.findIndex(
        (c) => c && c.path === path,
      );
      if (existingIdx !== -1) {
        state.breadcrumbs = state.breadcrumbs.slice(0, existingIdx + 1);
        return;
      }

      // Avoid duplicate push if already last
      const last = state.breadcrumbs[state.breadcrumbs.length - 1];
      if (last && last.path === path) {
        // Update label/type/id if provided and changed
        if (
          (label && label !== last.label) ||
          (typeof id !== "undefined" && id !== last.id) ||
          (type && type !== last.type)
        ) {
          state.breadcrumbs[state.breadcrumbs.length - 1] = {
            id: typeof id === "undefined" ? last.id : id,
            label: label || last.label,
            path,
            type: type || last.type,
          };
        }
        return;
      }

      state.breadcrumbs.push({ id, label, path, type });
      // Cap length to 10
      if (state.breadcrumbs.length > 10) {
        state.breadcrumbs = state.breadcrumbs.slice(
          state.breadcrumbs.length - 10,
        );
      }
    },
    setSelectedPreviewPage: (state, action) => {
      state.selectedPreviewPageId = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = Boolean(action.payload);
    },
    resetNavigation: () => initialState,
  },
});

export const {
  setStep,
  nextStep,
  prevStep,
  showValidationErrors,
  hideValidationErrors,
  setIsCreating,
  setRecentProjects,
  addRecentProject,
  setBreadcrumbs,
  resetBreadcrumbs,
  trimBreadcrumbs,
  pushBreadcrumb,
  setSelectedPreviewPage,
  toggleSidebar,
  setSidebarOpen,
  resetNavigation,
} = navigationSlice.actions;

export default navigationSlice.reducer;
